import { useEffect, useState } from "react";
import { Mail, Loader2, Send, ShieldCheck, AlertCircle, Trash2, Inbox, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * Investor "Bring-Your-Own" SMTP/IMAP setup. Lets the landlord plug their
 * own Gmail / Yahoo / Microsoft 365 / Outlook / custom server and route ALL
 * outbound platform mail through it. Once saved, includes a "Send test" button
 * to verify credentials end-to-end before going live.
 */
export default function EmailAccountSection() {
  const [presets, setPresets] = useState({});
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [provider, setProvider] = useState("gmail");
  const [form, setForm] = useState({
    host: "", port: 587, use_starttls: true, use_ssl: false,
    username: "", password: "", from_email: "", from_name: "", reply_to: "",
    imap_host: "", imap_port: 993, imap_ssl: true, enabled: true,
    signature: "",
  });
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [pr, acc] = await Promise.all([
        api.get("/email-account/presets"),
        api.get("/email-account"),
      ]);
      setPresets(pr.data?.presets || {});
      const a = acc.data?.account;
      if (a) {
        setAccount(a);
        setProvider(a.provider || "gmail");
        setForm({
          host: a.host || "", port: a.port || 587,
          use_starttls: a.use_starttls !== false, use_ssl: !!a.use_ssl,
          username: a.username || "",
          password: "", // always blank — server holds it encrypted
          from_email: a.from_email || "", from_name: a.from_name || "",
          reply_to: a.reply_to || "",
          imap_host: a.imap_host || "", imap_port: a.imap_port || 993,
          imap_ssl: a.imap_ssl !== false,
          enabled: a.enabled !== false,
          signature: a.signature || "",
        });
      } else {
        applyPreset("gmail", pr.data?.presets || {});
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load email settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const applyPreset = (key, presetMap) => {
    const map = presetMap || presets;
    const p = map[key];
    if (!p) return;
    setProvider(key);
    setForm((prev) => ({
      ...prev,
      host: p.host, port: p.port,
      use_starttls: !!p.use_starttls, use_ssl: !!p.use_ssl,
      imap_host: p.imap_host || "", imap_port: p.imap_port || 993,
      imap_ssl: p.imap_ssl !== false,
    }));
  };

  const onProviderChange = (key) => applyPreset(key, presets);

  const save = async () => {
    if (!form.from_email) { toast.error("From email required"); return; }
    if (!form.username) { toast.error("SMTP username required"); return; }
    if (!account && !form.password) { toast.error("Password required on first setup"); return; }
    setSaving(true);
    try {
      const body = {
        provider,
        host: form.host,
        port: Number(form.port) || 587,
        use_starttls: form.use_starttls,
        use_ssl: form.use_ssl,
        username: form.username,
        password: form.password || undefined,
        from_email: form.from_email,
        from_name: form.from_name || undefined,
        reply_to: form.reply_to || undefined,
        imap_host: form.imap_host || undefined,
        imap_port: Number(form.imap_port) || 993,
        imap_ssl: form.imap_ssl,
        enabled: form.enabled,
        signature: form.signature ?? "",
      };
      const r = await api.put("/email-account", body);
      setAccount(r.data?.account);
      setForm((prev) => ({ ...prev, password: "" }));
      toast.success("Email account saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo || !testTo.includes("@")) { toast.error("Enter a valid email address"); return; }
    setTesting(true);
    try {
      const r = await api.post("/email-account/test", { to: testTo });
      if (r.data?.ok) {
        toast.success(`Test email sent to ${testTo}`);
      } else {
        toast.error(r.data?.error || "Test send failed");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect this email account? Outgoing mail will revert to the platform default until you set up another account.")) return;
    try {
      await api.delete("/email-account");
      setAccount(null);
      setForm((prev) => ({ ...prev, password: "" }));
      toast.success("Email account disconnected");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Disconnect failed");
    }
  };

  if (loading) {
    return (
      <section className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="email-account-section">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading email settings…
        </div>
      </section>
    );
  }

  const hint = presets[provider]?.help;

  return (
    <section className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="email-account-section">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="overline text-slate-500">Outbound email</div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Use your own email account
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Route all platform email — tenant invites, rent receipts, lease notices, CPA exports — from your own Gmail, Yahoo, Microsoft 365, Outlook, or custom SMTP server.
          </p>
        </div>
        {account ? (
          <div className="text-right">
            <div className={`text-[11px] uppercase tracking-wider font-bold ${account.enabled ? "text-emerald-700" : "text-slate-500"}`}>
              {account.enabled ? "Connected" : "Disabled"}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 font-mono">{account.from_email}</div>
          </div>
        ) : null}
      </div>

      {/* Provider preset */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="email-provider-grid">
        {Object.entries(presets).map(([key, p]) => (
          <button
            key={key}
            type="button"
            onClick={() => onProviderChange(key)}
            className={`text-xs font-semibold px-3 py-2 rounded-sm border transition-colors ${provider === key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
            data-testid={`email-provider-${key}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {hint ? (
        <div className="bg-sky-50 border border-sky-200 rounded-sm px-3 py-2 text-xs text-sky-900 mb-4 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {hint}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <Field label="From email" required>
          <input
            type="email"
            className="input-clean w-full"
            value={form.from_email}
            onChange={(e) => setForm({ ...form, from_email: e.target.value })}
            placeholder="you@example.com"
            data-testid="email-from-email"
          />
        </Field>
        <Field label="From name (display)">
          <input
            type="text"
            className="input-clean w-full"
            value={form.from_name}
            onChange={(e) => setForm({ ...form, from_name: e.target.value })}
            placeholder="Your Name or Company"
            data-testid="email-from-name"
          />
        </Field>
        <Field label="SMTP username" required>
          <input
            type="text"
            className="input-clean w-full font-mono"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            placeholder="usually your full email address"
            data-testid="email-username"
          />
        </Field>
        <Field label={account ? "Password (leave blank to keep)" : "Password / App password"} required={!account}>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              className="input-clean w-full pr-10"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={account ? "•••••••• (kept)" : "App password recommended"}
              data-testid="email-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              tabIndex={-1}
              aria-label="Toggle password visibility"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </Field>
        <Field label="Reply-to address (optional)">
          <input
            type="email"
            className="input-clean w-full"
            value={form.reply_to}
            onChange={(e) => setForm({ ...form, reply_to: e.target.value })}
            data-testid="email-reply-to"
          />
        </Field>
      </div>

      {/* Signature — appended to every outgoing compose & reply */}
      <div className="mb-4">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-slate-600 mb-1 block flex items-center justify-between" style={{ fontFamily: "Outfit" }}>
            <span>Email signature (optional)</span>
            <span className={`font-mono text-[10px] ${(form.signature || "").length > 1800 ? "text-amber-600" : "text-slate-400"}`} data-testid="email-signature-counter">
              {(form.signature || "").length}/2000
            </span>
          </span>
          <textarea
            rows={4}
            className="input-clean w-full font-sans"
            value={form.signature || ""}
            onChange={(e) => setForm({ ...form, signature: e.target.value.slice(0, 2000) })}
            placeholder={"e.g.\nJane Smith — Managing Partner\nSmith Capital · 555-123-4567\nwww.smithcapital.com"}
            data-testid="email-signature"
          />
        </label>
        <p className="text-[11px] text-slate-500 mt-1">
          Plain text. Auto-appended to every email sent from RealtyVault Manager — including replies and tenant invites.
        </p>
      </div>

      {/* Server fields (collapsible-feeling area for advanced) */}
      <details className="mb-4 group" data-testid="email-server-details">
        <summary className="cursor-pointer text-xs font-semibold text-slate-700 hover:text-slate-900 select-none">
          Advanced: server settings
        </summary>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <Field label="SMTP host">
            <input type="text" className="input-clean w-full font-mono" value={form.host}
                   onChange={(e) => setForm({ ...form, host: e.target.value })} data-testid="email-host" />
          </Field>
          <Field label="SMTP port">
            <input type="number" min="1" max="65535" className="input-clean w-full font-mono" value={form.port}
                   onChange={(e) => setForm({ ...form, port: e.target.value })} data-testid="email-port" />
          </Field>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={form.use_starttls}
                   onChange={(e) => setForm({ ...form, use_starttls: e.target.checked, use_ssl: e.target.checked ? false : form.use_ssl })}
                   data-testid="email-use-starttls" />
            Use STARTTLS (port 587)
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={form.use_ssl}
                   onChange={(e) => setForm({ ...form, use_ssl: e.target.checked, use_starttls: e.target.checked ? false : form.use_starttls })}
                   data-testid="email-use-ssl" />
            Use SSL (port 465)
          </label>
          <Field label="IMAP host (for inbox)">
            <input type="text" className="input-clean w-full font-mono" value={form.imap_host}
                   onChange={(e) => setForm({ ...form, imap_host: e.target.value })} data-testid="email-imap-host" />
          </Field>
          <Field label="IMAP port">
            <input type="number" className="input-clean w-full font-mono" value={form.imap_port}
                   onChange={(e) => setForm({ ...form, imap_port: e.target.value })} data-testid="email-imap-port" />
          </Field>
        </div>
      </details>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary-dark inline-flex items-center gap-2"
          data-testid="email-account-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {saving ? "Saving…" : account ? "Update settings" : "Connect account"}
        </button>

        {account ? (
          <>
            <div className="flex items-center gap-1.5 ml-2">
              <input
                type="email"
                className="input-clean text-xs"
                placeholder="test@example.com"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                data-testid="email-test-to"
              />
              <button
                onClick={sendTest}
                disabled={testing}
                className="btn-outline-dark text-xs inline-flex items-center gap-1.5"
                data-testid="email-test-send"
              >
                {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                {testing ? "Sending…" : "Send test"}
              </button>
            </div>
            <button
              onClick={disconnect}
              className="ml-auto text-xs font-semibold text-rose-700 hover:text-rose-900 inline-flex items-center gap-1"
              data-testid="email-account-disconnect"
            >
              <Trash2 className="w-3 h-3" /> Disconnect
            </button>
          </>
        ) : null}
      </div>

      {account ? (
        <div className="mt-3 text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid="email-mail-links">
          <Mail className="w-3.5 h-3.5" />
          Browse your mail in the app:
          <a href="/dashboard/mail/inbox" className="font-semibold text-slate-900 hover:underline inline-flex items-center gap-1">
            <Inbox className="w-3 h-3" /> Inbox
          </a>
          ·
          <a href="/dashboard/mail/sent" className="font-semibold text-slate-900 hover:underline inline-flex items-center gap-1">
            <Send className="w-3 h-3" /> Sent box
          </a>
        </div>
      ) : null}
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-slate-600 mb-1 block" style={{ fontFamily: "Outfit" }}>
        {label}{required ? <span className="text-rose-600">*</span> : null}
      </span>
      {children}
    </label>
  );
}
